import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, isFieldRole } from "./context/AuthContext";
import Protected from "./components/Protected";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Physios from "./pages/Physios";
import Wallets from "./pages/Wallets";
import Milestones from "./pages/Milestones";
import Referrals from "./pages/Referrals";
import Rewards from "./pages/Rewards";
import Campaigns from "./pages/Campaigns";
import WhatsAppStudio from "./pages/WhatsAppStudio";
import Grievances from "./pages/Grievances";
import Inventory from "./pages/Inventory";
import Audit from "./pages/Audit";
import Users from "./pages/Users";
import Tenants from "./pages/Tenants";
import Config from "./pages/Config";
import Hierarchy from "./pages/Hierarchy";
import AIInsights from "./pages/AIInsights";
import Onboarding from "./pages/Onboarding";
import SalesDashboard from "./pages/SalesDashboard";
import Approvals from "./pages/Approvals";
import Fulfillment from "./pages/Fulfillment";
import NonServicedAlerts from "./pages/NonServicedAlerts";
import AdminImports from "./pages/AdminImports";
import HierarchyRollup from "./pages/HierarchyRollup";
import ProductCatalog from "./pages/ProductCatalog";
import Cohorts from "./pages/Cohorts";
import Training from "./pages/Training";
import TransactionReport from "./pages/TransactionReport";
import RegistrationReport from "./pages/RegistrationReport";
import SampleReport from "./pages/SampleReport";
import RedemptionTracking from "./pages/RedemptionTracking";
import CustomerSearch from "./pages/CustomerSearch";
import FieldLogins from "./pages/FieldLogins";
import WhatsAppMessages from "./pages/WhatsAppMessages";
import PublicRewards from "./pages/PublicRewards";

// Role-aware landing: field users (DSE / Zonal) land on the Sales/DSE Dashboard
// since the Executive Dashboard is hidden for them.
function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={isFieldRole(user.role) ? "/sales" : "/dashboard"} replace />;
}

// Blocks field-mobile users from reaching restricted routes by direct URL.
function FieldBlocked({ children }) {
  const { user } = useAuth();
  if (user && isFieldRole(user.role)) return <Navigate to="/sales" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/r/:points" element={<PublicRewards />} />
          <Route path="/r/program/:programId" element={<PublicRewards />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/dashboard" element={<FieldBlocked><Dashboard /></FieldBlocked>} />
            <Route path="/sales" element={<SalesDashboard />} />
            <Route path="/hierarchy" element={<Hierarchy />} />
            <Route path="/ai-insights" element={<AIInsights />} />
            <Route path="/wallets" element={<Wallets />} />
            <Route path="/milestones" element={<Milestones />} />
            <Route path="/referrals" element={<Referrals />} />
            <Route path="/physios" element={<Physios />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/fulfillment" element={<Fulfillment />} />
            <Route path="/alerts" element={<FieldBlocked><NonServicedAlerts /></FieldBlocked>} />
            <Route path="/rewards" element={<FieldBlocked><Rewards /></FieldBlocked>} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/whatsapp" element={<FieldBlocked><WhatsAppStudio /></FieldBlocked>} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/grievances" element={<Grievances />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/users" element={<Users />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="/config" element={<Config />} />
            <Route path="/admin/imports" element={<AdminImports />} />
            <Route path="/my-rollup" element={<HierarchyRollup />} />
            <Route path="/products" element={<ProductCatalog />} />
            <Route path="/cohorts" element={<Cohorts />} />
            <Route path="/reports/transactions" element={<TransactionReport />} />
            <Route path="/reports/registrations" element={<RegistrationReport />} />
            <Route path="/reports/samples" element={<SampleReport />} />
            <Route path="/reports/redemptions" element={<RedemptionTracking />} />
            <Route path="/admin/customer-search" element={<FieldBlocked><CustomerSearch /></FieldBlocked>} />
            <Route path="/admin/field-logins" element={<FieldBlocked><FieldLogins /></FieldBlocked>} />
            <Route path="/whatsapp/messages" element={<FieldBlocked><WhatsAppMessages /></FieldBlocked>} />
            <Route path="/training" element={<Training />} />
          </Route>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
